import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import Footer from './pages/HomePage/Footer';
import styled from 'styled-components';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { OAUTH_CONFIG } from './config/index';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load components
const HomePage = React.lazy(() => import('./pages/HomePage/HomePage'));
const News = React.lazy(() => import('./pages/News/News'));
const NewsDetail = React.lazy(() => import('./pages/NewsDetail/NewsDetail'));
const ShortVideos = React.lazy(() => import('./pages/ShortVideos/ShortVideos'));
const VideoDetail = React.lazy(() => import('./pages/VideoDetail/VideoDetail'));
const Login = React.lazy(() => import('./pages/Login/Login'));
const Register = React.lazy(() => import('./pages/Register/Register'));
const NewsForm = React.lazy(() => import('./pages/NewsForm/NewsForm'));
const Help = React.lazy(() => import('./pages/Help/Help'));
const Privacy = React.lazy(() => import('./pages/Privacy/Privacy'));
const Contact = React.lazy(() => import('./pages/Contact/Contact'));
const SAA = React.lazy(() => import('./pages/SAA/SAA'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));
const UserProfile = React.lazy(() => import('./pages/UserProfile/UserProfile'));
const ExtractPDF = React.lazy(() => import('./pages/ExtractPDF/ExtractPDF'));
const PostDetail = React.lazy(() => import('./pages/PostDetail/PostDetail'));
const Posts = React.lazy(() => import('./pages/Posts/Posts'));
const Study = React.lazy(() => import('./pages/Study/Study'));

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
`;

const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh' 
  }}>
    <LoadingSpinner />
  </div>
);

// Wrap each route with the layout components
const withLayout = (Component) => {
  return (
    <AppContainer>
      <MainContent>
        <Component />
      </MainContent>
      <Footer />
    </AppContainer>
  );
};

// Create protected route wrapper
const withProtectedRoute = (Component) => {
  return (
    <ProtectedRoute>
      {withLayout(Component)}
    </ProtectedRoute>
  );
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Disable automatic refetching on window focus
    },
  },
});

const App = () => {
    return (
      <GoogleOAuthProvider clientId={OAUTH_CONFIG.GOOGLE.CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Suspense fallback={<LoadingFallback />}>
              <RouterProvider router={router} />
            </Suspense>
          </AuthProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    );
};

const router = createBrowserRouter([
    {
        path: '/',
        element: withLayout(HomePage),
    },
    {
        path: '/news',
        element: withProtectedRoute(News),
    },
    {
        path: '/news/:id',
        element: withProtectedRoute(NewsDetail),
    },
    {
        path: '/videos',
        element: withProtectedRoute(ShortVideos),
    },
    {
        path: '/videos/:id',
        element: withProtectedRoute(VideoDetail),
    },
    {
        path: '/login',
        element: withLayout(Login),
    },
    {
        path: '/register',
        element: withLayout(Register),
    },
    {
        path: '/create-post',
        element: withProtectedRoute(NewsForm),
    },
    {
        path: '/help',
        element: withLayout(Help),
    },
    {
        path: '/privacy',
        element: withLayout(Privacy),
    },
    {
        path: '/contact',
        element: withLayout(Contact),
    },
    {
        path: '/saa',
        element: withLayout(SAA),
    },
    {
        path: '/user/profile',
        element: withProtectedRoute(UserProfile),
    },
    {
        path: '/extract-pdf',
        element: withProtectedRoute(ExtractPDF),
    },
    {
        path: '/posts',
        element: withProtectedRoute(Posts),
    },
    {
        path: '/posts/:id',
        element: withProtectedRoute(PostDetail),
    },
    {
        path: '/study',
        element: withLayout(Study),
    },
    {
        path: '*',
        element: withLayout(NotFound),
    },
]);

export default App;
